import * as React from 'react';
import styled from 'styled-components';
import { pipe, tuple, Lazy } from 'fp-ts/lib/function';
import * as IO from 'fp-ts/lib/IO';
import * as IOE from 'fp-ts/lib/IOEither';
import { sequenceT } from 'fp-ts/lib/Apply';

import { 
    Refable, Windowable, Errorable, Documentable, 
    extractNode, extractDocument,
    getScreenPosition, getDocumentPosition,
    getSize,
} from './Env';
import { useErrorable, useLooper, useIORefState } from './hooks';

type PositionMode = 'screen' | 'document';

interface Position {
    readonly top: number,
    readonly left: number,
}

interface Size {
    readonly width: number,
    readonly height: number,
}

interface ComputedPosition {
    (offset: number, size: number): number
}

export interface TracePosition {
    top: ComputedPosition,
    left: ComputedPosition,
}

export interface TraceSize {
    (screenSize: number, targetSize: number, offsetLeft: number): number
}

const emptyPosition = {
    top: 0,
    left: 0,
}

const emptySize = {
    width: 0,
    height: 0,
}

// -------------------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------------------

const isFunction = (x: any): x is Function => typeof(x) === 'function';

export const startAlign = (offset: number, _: number) => offset;
export const centerAlign = (offset: number, size: number) => offset + size/2;
export const endAlign = (offset: number, size: number) => offset + size;
export const halfSize = (size: number) => size / 2;
export const fullSize = (size: number) => size;
export const doubleSize = (size: number) => size * 2;


export const pos1: TracePosition = {
    top: startAlign,
    left: startAlign,
}

export const pos2: TracePosition = {
    top: startAlign,
    left: centerAlign,
}

export const pos3: TracePosition = {
    top: startAlign,
    left: endAlign,
}

export const pos4: TracePosition = {
    top: centerAlign,
    left: startAlign,
}

export const pos5: TracePosition = {
    top: centerAlign,
    left: centerAlign,
}

export const pos6: TracePosition = {
    top: centerAlign,
    left: endAlign,
}

export const pos7: TracePosition = {
    top: endAlign,
    left: startAlign,
}

export const pos8: TracePosition = {
    top: endAlign,
    left: centerAlign,
}

export const pos9: TracePosition = {
    top: endAlign,
    left: endAlign,
}


/**
 * возвращает всё расстояние между отступом слева и правым краем экрана
 * @param W - ширина экрана
 * @param _ - ширина элемента
 * @param offset - отступ слева
 */
export const size1 = (W: number, _: number, offset: number) => W - offset;

/**
 * возвращает отступ слева
 * @param _1 - ширина экрана
 * @param _2 - ширина элемента
 * @param offset - отступ слева
 */
export const size2 = (_1: number, _2: number, offset: number) => offset;


/**
 * возвращает ширину элемента
 * @param _ - ширина экрана
 * @param w - ширина элемента
 * @param offset - отступ слева
 */
export const size3 = (_: number, w: number, __: number) => w;







interface AlgebrasesPosition {
    tracePosition: TracePosition,
    getMode: () => PositionMode,
}

const corePosition = <F extends HTMLElement>(env: Refable<F> & Windowable & Errorable & AlgebrasesPosition): IO.IO<Readonly<Position>> =>
    pipe(
        extractNode(env),
        IOE.fold(
            () => () => {
                env.exception();
                return emptyPosition;
            },
            (el) => pipe(
                () => el,
                IO.map(el => pipe(
                    env.getMode(),
                    (mode) => {
                        switch(mode){
                            case 'screen': 
                                return getScreenPosition(el);
                            case 'document':
                                return getDocumentPosition(env)(el);
                        }
                    },
                    (position) => tuple(position(), getSize(el)()),
                    ([position, size]) => ({
                        top: env.tracePosition.top(position.top, size.height),
                        left: env.tracePosition.left(position.left, size.width),
                    }),
                )),
            ),
        ),
    );

const getRectNode = <F extends HTMLElement>(env: Refable<F>) =>
    pipe(
        extractNode(env),
        IOE.map(el => el.getBoundingClientRect()),
        IOE.fold(
            () => () => new DOMRect(),
            (r) => () => r,
        ),
    )();



type PositionSettings = {
    /**
     * Ссылка на элемент, относительно которого ведём расчёт
     */
    target: React.RefObject<HTMLElement>,

    /**
     * Координаты расчитываем относительно экрана или документа?
     */
    relative: 'screen' | 'document',

    /**
     * Указываем точку отсчёта у элемента
     */
    position: TracePosition,

    /**
     * Хук, срабатывающий при каждом расчёте
     */
    onDetector?: (rect: DOMRect) => void,
}

export const usePosition = (settings: PositionSettings) => {
    const [error, exception] = useErrorable();
    const [{
        getValue: getResult,
    }, setResult] = useIORefState(emptyPosition);

    const env = {
        ref: settings.target,
        window,
        tracePosition: settings.position,
        exception,
        getMode: () => settings.relative,
    }

    const [onCalc, offCalc] = useLooper(() => {
        const x = corePosition(env)();
        setResult(x);
        
        
        if(isFunction(settings.onDetector)){
            const rect = getRectNode(env);
            settings.onDetector(rect);
        }
    });


    return tuple(error, {
        onCalc,
        offCalc,
        getResult,
    });
}




interface AlgebrasesSize {
    traceSize: TraceSize,
    getOffset: () => number,
}



const getDocumentWidth = (html: HTMLElement) => html.clientWidth - 5;

const coreSize = <F extends HTMLElement>(env: Refable<F> & Documentable & Errorable & AlgebrasesSize): IO.IO<Size> =>
    pipe(
        sequenceT(IOE.ioEither)(extractNode(env), pipe(extractDocument(env), IOE.map(d => d.documentElement))),
        IOE.fold(
            () => () => {
                env.exception();
                return emptySize;
            },
            ([el, html]) => () => {
                const screenSize = getDocumentWidth(html);
                const targetSize = getSize(el)();
                const offsetLeft = env.getOffset();
                const width = env.traceSize(screenSize, targetSize.width, offsetLeft);
                const height = targetSize.height;

                return {
                    width,
                    height,
                };
            },
        )
    );


type SizeSettings = {
    /**
     * Ссылка на элемент, относительно которого ведём расчёт
     */
    target: React.RefObject<HTMLElement>,

    /**
     * Функция взятия результирующей ширины на основе ширины экрана, ширины самого элемента и отступа слева
     */
    takeWidth: TraceSize,

    /**
     * Позиция элемента
     */
    offset: Lazy<Position>,

    /**
     * Хук, срабатывающий при каждом расчёте
     */
    onDetector?: (rect: DOMRect) => void,
}

export const useSize = (settings: SizeSettings) => {
            const [error, exception] = useErrorable();
            const [{
                getValue: getResult,
            }, setResult] = useIORefState(emptySize);

            const env = {
                ref: settings.target,
                document,
                exception,
                traceSize: settings.takeWidth,
                getOffset: () => settings.offset().left,
            }

            const [onCalc, offCalc] = useLooper(() => {
                const run = coreSize(env);
                const x = run();

                setResult(x);
        
                if(isFunction(settings.onDetector)){
                    const rect = getRectNode(env);
                    settings.onDetector(rect);
                }
            });

            return tuple(error, {
                onCalc,
                offCalc,
                getResult,
            });
        }


export const Obj = styled.div`
    position: absolute;
    z-index: 500;
`;
